import { useTranslator } from "@eo-locale/react";

import { useEffect } from "react";

import { useRouter } from "next/router";

import Cookies from "js-cookie";

import { Flex, HTMLWrapper, Field, Alert } from "@/storybook-components";
import AccountEmailQuestion from "@/components/questions/account-email-question";
import LoginQuestion from "@/components/questions/login-question";
import TosQuestion from "@/components/questions/tos-question";
import useUser from "@/hooks/useUser";
import { useAppSelector, useAppDispatch } from "@/redux/store/hooks";
import { updateUnverifiedUser } from "@/redux/features/user/slice";
import { setAnswer } from "@/redux/features/questions/slice";

const UnverifiedForm = ({
  isAsterixRequired,
  hasErrorInSubmission
}): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { isUnverfiedUserLoggedIn } = useUser();
  const { answers, errors } = useAppSelector((state) => state.questions);

  useEffect(() => {
    // if an unverified user has already contributed
    if (isUnverfiedUserLoggedIn) {
      const questions = ["email", "login", "tosQuestion"];
      questions.forEach((questionId) => {
        dispatch(
          setAnswer({
            questionId,
            answer: answers?.[questionId]
          })
        );
      });
    }
  }, []);

  useEffect(() => {
    dispatch(
      updateUnverifiedUser({
        email: answers?.email,
        login: answers?.login,
        tosQuestion: answers?.tosQuestion
      })
    );
  }, [answers, dispatch]);

  const getRoute = (route: "login" | "register") => {
    const cookieValue = Cookies.get("trigger_point");
    let trigger;
    try {
      trigger = JSON.parse(cookieValue);
      // eslint-disable-next-line no-unused-vars
    } catch (e) {
      trigger = {};
    }
    const routerPath = router.asPath;
    if (trigger?.triggeredSource === "comment") {
      return `/${route}?next=${encodeURIComponent(`${routerPath}&comment=true`)}`;
    }

    return `/${route}?next=${encodeURIComponent(routerPath)}`;
  };

  return (
    <Field.Root
      display="flex"
      flexDirection="column"
      gap={6}
      px={1}
      css={{
        "> p": { margin: 0 },
        "> div": { margin: 0 },
        ".fr-view": {
          fontSize: "sm",
          fontWeight: "semibold",
          color: "gray.800"
        },
        "& input": {
          background: "white",
          fontSize: "md"
        },
        ".unverified-user-modal": {
          alignItems: "flex-start",
          fontWeight: "normal"
        },
        ".unverified-user-modal .fr-view": {
          fontWeight: "normal"
        }
      }}
    >
      <Flex
        flexDirection="column"
        gap={4}
        css={{
          "& > div": { margin: 0 },
          "[data-part='helper-text']": { marginTop: 0 },
          "& ::placeholder": {
            color: "gray.600"
          }
        }}
      >
        <AccountEmailQuestion
          question={{
            id: "email",
            question: translate("common.email"),
            required: true,
            maxLength: 140,
            placeholder: translate("brainstormers.emailPlaceholder")
          }}
          isAsterixRequired={isAsterixRequired}
        />
        <LoginQuestion
          question={{
            id: "login",
            question: translate("common.screenName"),
            required: true
          }}
          isAsterixRequired={isAsterixRequired}
        />
        <TosQuestion
          question={{
            id: "tosQuestion",
            question: `${translate("common.termsAndConditions", {
              target: "_blank"
            })}`,
            required: true
          }}
          isAsterixRequired={isAsterixRequired}
        />
      </Flex>

      <HTMLWrapper
        className="unverified-user-modal"
        html={translate("brainstormers.signInOrRegisterText", {
          loginRoute: getRoute("login"),
          registerRoute: getRoute("register")
        })}
      />

      {hasErrorInSubmission &&
        !(
          answers?.login &&
          answers?.tosQuestion &&
          answers?.email &&
          !errors?.email
        ) && (
          <Alert.Root status="error" variant="solid" borderRadius="md">
            <Alert.Indicator />
            {translate("errors.OnUnverifiedModalRequiredError")}
          </Alert.Root>
        )}
    </Field.Root>
  );
};

export default UnverifiedForm;
